/**
 * Market simulation with bot traders for educational sandbox mode.
 * Creates realistic-looking market activity for users to observe and trade against.
 */

import type { NewOrder, OrderBook } from './types'

/**
 * Configuration for the market simulation.
 */
export interface SimulationConfig {
  /** Base probability the bots trade around (0-1) */
  baseProbability: number
  /** How volatile the bot activity is (0-1, higher = more variation) */
  volatility: number
  /** Average orders per second when running at 1x speed */
  ordersPerSecond: number
}

/**
 * Default simulation configuration for a balanced market.
 */
export const DEFAULT_SIMULATION_CONFIG: SimulationConfig = {
  baseProbability: 0.5,
  volatility: 0.1,
  ordersPerSecond: 0.5, // One order every 2 seconds on average
}

/**
 * Generate a random bot order based on current market state.
 */
export function generateBotOrder(
  orderBook: OrderBook,
  config: SimulationConfig = DEFAULT_SIMULATION_CONFIG
): NewOrder | null {
  // Get current market state
  const bestBid = orderBook.bids.length > 0
    ? Math.max(...orderBook.bids.map(o => o.price))
    : config.baseProbability - 0.05

  const bestAsk = orderBook.asks.length > 0
    ? Math.min(...orderBook.asks.map(o => o.price))
    : config.baseProbability + 0.05

  const midPrice = (bestBid + bestAsk) / 2

  // Random variation around the base probability
  const targetPrice = config.baseProbability + (Math.random() - 0.5) * config.volatility * 2

  // Decide if bot wants to buy or sell based on their target vs market
  const wantsToBuy = targetPrice > midPrice
  const side: 'buy' | 'sell' = wantsToBuy ? 'buy' : 'sell'

  // Decide order type (70% limit, 30% market for more realistic activity)
  const useMarketOrder = Math.random() < 0.3

  // Generate price for limit orders
  let price: number
  if (side === 'buy') {
    // Buyers place orders slightly below or at the ask
    price = useMarketOrder
      ? 0 // Market orders don't use price
      : Math.min(bestAsk, targetPrice + Math.random() * 0.02)
  } else {
    // Sellers place orders slightly above or at the bid
    price = useMarketOrder
      ? 0
      : Math.max(bestBid, targetPrice - Math.random() * 0.02)
  }

  // Clamp price to valid range
  price = Math.max(0.01, Math.min(0.99, price))
  price = Math.round(price * 100) / 100 // Round to cents

  // Random quantity (small orders for educational purposes)
  const quantity = Math.floor(Math.random() * 20) + 5 // 5-25 shares

  // Skip if market order but no liquidity
  if (useMarketOrder) {
    if (side === 'buy' && orderBook.asks.length === 0) return null
    if (side === 'sell' && orderBook.bids.length === 0) return null
  }

  return {
    side,
    type: useMarketOrder ? 'market' : 'limit',
    price,
    quantity,
  }
}

/**
 * Create a simulation runner that generates bot orders at intervals.
 */
export class MarketSimulator {
  private intervalId: ReturnType<typeof setInterval> | null = null
  private config: SimulationConfig
  private onOrder: (order: NewOrder) => void
  private getOrderBook: () => OrderBook | null
  private speedMultiplier: number = 1

  constructor(
    onOrder: (order: NewOrder) => void,
    getOrderBook: () => OrderBook | null,
    config: SimulationConfig = DEFAULT_SIMULATION_CONFIG
  ) {
    this.onOrder = onOrder
    this.getOrderBook = getOrderBook
    this.config = config
  }

  /**
   * Update the simulation configuration.
   */
  setConfig(config: SimulationConfig): void {
    this.config = config
  }

  /**
   * Start the simulation at the given speed multiplier.
   */
  start(speedMultiplier: number = 1): void {
    this.stop()
    this.speedMultiplier = speedMultiplier

    if (this.speedMultiplier === 0) return

    // Calculate interval based on orders per second and speed
    const baseInterval = 1000 / this.config.ordersPerSecond
    const adjustedInterval = baseInterval / speedMultiplier

    // Minimum 200ms interval to prevent overwhelming the UI
    const interval = Math.max(200, adjustedInterval)

    this.intervalId = setInterval(() => {
      const orderBook = this.getOrderBook()
      if (!orderBook) return

      // Random chance to place an order (adds variability)
      if (Math.random() < 0.7) {
        const order = generateBotOrder(orderBook, this.config)
        if (order) {
          this.onOrder(order)
        }
      }
    }, interval)
  }

  /**
   * Stop the simulation.
   */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  /**
   * Check if the simulation is running.
   */
  isRunning(): boolean {
    return this.intervalId !== null
  }

  /**
   * Update the speed multiplier while running.
   */
  setSpeed(speedMultiplier: number): void {
    if (this.isRunning()) {
      this.start(speedMultiplier)
    }
  }

  /**
   * Clean up resources.
   */
  destroy(): void {
    this.stop()
  }
}
